/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import { type Command, CAPACITY_UNIT, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract, UtilService } from '@dm/core';
import { ToolService } from 'app/common/services';
import { Quota } from 'app/common/types/mo';
import { isUndefined } from 'lodash';

import { type QuotaVm } from '../user-quota';
import { UserQuotaService } from '../user-quota.service';
import { UserQuotaStrService } from '../user-quota-str.service';

@Component({
    selector: 'app-modify-user-quota',
    templateUrl: './modify-user-quota.component.html',
    styleUrls: ['./modify-user-quota.component.scss'],
})
export class ModifyUserQuotaComponent extends FormSlideWindowAbstract implements OnInit {
    @Input() data: Quota;
    /**
     * 前台表格传入true
     */
    @Input() isFrontTable = false;
    /**
     * 是否是配额报表页面
     */
    @Input() isQuotaReports = false;

    i18n = this.str.i18n;

    vm: FormGroup;
    errorMessage: any;

    get vmValue(): QuotaVm {
        return this.vm ? this.vm.value : null;
    }

    constructor(
        public str: UserQuotaStrService,
        private service: UserQuotaService,
        injector: Injector,
        private toolService: ToolService,
        private utilService: UtilService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.pageReady = true;
        this.windowVisible = true;
    }

    initVm(): void {
        const spaceQuotaValidator = DMValidators.capacityValidator(false, this.str.spaceQuotaMin, this.str.spaceQuotaMax);

        this.vm = new FormGroup({
            spaceQuota: new FormGroup({
                hard: new FormControl({ value: '', unit: 'MB' }, {
                    validators: [
                        spaceQuotaValidator,
                    ],
                }),
                soft: new FormControl({ value: '', unit: 'MB' }, {
                    validators: [
                        spaceQuotaValidator,
                    ],
                }),
            }, { validators: [this.service.spaceCompareValidator] }),
            fileQuota: new FormGroup({
                hard: new FormControl(null),
                hardUnit: new FormControl(1000),
                soft: new FormControl(null),
                softUnit: new FormControl(1000),
            }, { validators: [this.service.fileCompareValidator] }),
        }, {
            updateOn: 'blur',
        });

        const spaceHardQuotaErr = this.toolService.rangeErr(this.i18n.hardQuotaLab, this.str.spaceQuotaMin, this.str.spaceQuotaMax);
        const spaceSoftQuotaErr = this.toolService.rangeErr(this.i18n.softQuotaLab, this.str.spaceQuotaMin, this.str.spaceQuotaMax);
        const fileHardQuotaErr = this.toolService.rangeErr(this.i18n.hardQuotaLab, this.str.fileQuotaMin,
            this.str.billion(this.i18nService.language === 'zh' ? '20' : '2'));
        const fileSoftQuotaErr = this.toolService.rangeErr(this.i18n.softQuotaLab, this.str.fileQuotaMin,
            this.str.billion(this.i18nService.language === 'zh' ? '20' : '2'));

        this.errorMessage = {
            spaceQuota: {
                hard: {
                    min: spaceHardQuotaErr,
                    max: spaceHardQuotaErr,
                },
                soft: {
                    min: spaceSoftQuotaErr,
                    max: spaceSoftQuotaErr,
                    quotaCompare: this.i18n.quotaCompareErr,
                },
            },
            fileQuota: {
                hard: {
                    fileHardQuota: fileHardQuotaErr,
                },
                hardUnit: {
                    fileHardQuota: fileHardQuotaErr,
                },
                soft: {
                    fileSoftQuota: fileSoftQuotaErr,
                    quotaCompare: this.i18n.quotaCompareErr,
                },
                softUnit: {
                    fileSoftQuota: fileSoftQuotaErr,
                },
            },
        };

        if (!this.toolService.isInvalidNum(this.data.SPACEHARDQUOTA, true) && !isUndefined(this.data.SPACEHARDQUOTA)) {
            const spaceQuota = this.utilService.adaptUnit(
                this.data.SPACEHARDQUOTA, CAPACITY_UNIT.KB,
                undefined,
                undefined,
                undefined,
                true,
            );
            this.vm.get('spaceQuota.hard').setValue({ value: spaceQuota.value, unit: spaceQuota.unit });
        }
        if (!this.toolService.isInvalidNum(this.data.SPACESOFTQUOTA, true) && !isUndefined(this.data.SPACESOFTQUOTA)) {
            const spaceQuota = this.utilService.adaptUnit(
                this.data.SPACESOFTQUOTA,
                CAPACITY_UNIT.KB,
                undefined,
                undefined,
                undefined,
                true,
            );
            this.vm.get('spaceQuota.soft').setValue({ value: spaceQuota.value, unit: spaceQuota.unit });
        }
        if (!this.toolService.isInvalidNum(this.data.FILEHARDQUOTA, true) && !isUndefined(this.data.FILEHARDQUOTA)) {
            const { value, unit } = this.str.randerFileQuota(this.data.FILEHARDQUOTA);
            this.vm.get('fileQuota.hard').setValue(value);
            this.vm.get('fileQuota.hardUnit').setValue(unit);
        }
        if (!this.toolService.isInvalidNum(this.data.FILESOFTQUOTA, true) && !isUndefined(this.data.FILESOFTQUOTA)) {
            const { value, unit } = this.str.randerFileQuota(this.data.FILESOFTQUOTA);
            this.vm.get('fileQuota.soft').setValue(value);
            this.vm.get('fileQuota.softUnit').setValue(unit);
        }

        // 保存初始状态
        this.vm.restore();
    }

    /**
     * 表单是否被修改
     */
    isModified(): boolean {
        if (!this.vm.rowValueChanged) {
            return false;
        }

        const changedValue: QuotaVm = this.vm.changedRawValue;
        const vmValue: QuotaVm = this.vm.value;
        const lastValue: QuotaVm = this.vm.lastValue;

        if (Object.prototype.hasOwnProperty.call(changedValue, 'spaceQuota')) {
            this.checkIsSpaceQuotaChanged(changedValue, vmValue, lastValue);
        }

        if (Object.prototype.hasOwnProperty.call(changedValue, 'fileQuota')) {
            this.checkIsFileQuotaChanged(changedValue, vmValue, lastValue);
        }

        return Object.keys(changedValue).length > 0;
    }

    /**
     * 检测容量配额值是否真的改变了（换算单位后比较）
     */
    checkIsSpaceQuotaChanged(changedValue: QuotaVm, vmValue: QuotaVm, lastValue: QuotaVm): void {
        const hardValue = this.utilService.adaptUnit(
            vmValue.spaceQuota.hard.value,
            vmValue.spaceQuota.hard.unit,
            CAPACITY_UNIT.KB,
        ).value;
        const lastHardValue = this.utilService.adaptUnit(
            lastValue.spaceQuota.hard.value,
            lastValue.spaceQuota.hard.unit,
            CAPACITY_UNIT.KB,
        ).value;

        if (hardValue === lastHardValue) {
            delete changedValue.spaceQuota.hard;
        }

        const softValue = this.utilService.adaptUnit(
            vmValue.spaceQuota.soft.value,
            vmValue.spaceQuota.soft.unit,
            CAPACITY_UNIT.KB,
        ).value;
        const lastSoftValue = this.utilService.adaptUnit(
            lastValue.spaceQuota.soft.value,
            lastValue.spaceQuota.soft.unit,
            CAPACITY_UNIT.KB,
        ).value;

        if (softValue === lastSoftValue) {
            delete changedValue.spaceQuota.soft;
        }

        if (Object.keys(changedValue.spaceQuota).length === 0) {
            delete changedValue.spaceQuota;
        }
    }

    /**
     * 检测文件配额值是否真的改变了（换算单位后比较）
     */
    checkIsFileQuotaChanged(changedValue: QuotaVm, vmValue: QuotaVm, lastValue: QuotaVm): void {
        const fileHardValue = vmValue.fileQuota.hard * vmValue.fileQuota.hardUnit;
        const lastFileHardValue = lastValue.fileQuota.hard * lastValue.fileQuota.hardUnit;

        if (fileHardValue === lastFileHardValue) {
            delete changedValue.fileQuota.hard;
            delete changedValue.fileQuota.hardUnit;
        }

        const fileSoftValue = vmValue.fileQuota.soft * vmValue.fileQuota.softUnit;
        const lastFileSoftValue = lastValue.fileQuota.soft * lastValue.fileQuota.softUnit;

        if (fileSoftValue === lastFileSoftValue) {
            delete changedValue.fileQuota.soft;
            delete changedValue.fileQuota.softUnit;
        }
        if (Object.keys(changedValue.fileQuota).length === 0) {
            delete changedValue.fileQuota;
        }
    }

    /**
     * 构建下发命令
     */
    buildCmds(): Command[] {
        const cmds: Command[] = [];

        const vmValue: QuotaVm = this.vmValue;
        const params: Quota = {
            SPACEUNITTYPE: '1', // 指定容量单位为KB
        };
        // 用64位的非法值表示清空某个配额
        const invaliNum = '0';

        if (vmValue.spaceQuota.hard.value) {
            params.SPACEHARDQUOTA = String(this.utilService.adaptUnit(
                vmValue.spaceQuota.hard.value,
                vmValue.spaceQuota.hard.unit,
                CAPACITY_UNIT.KB,
            ).value);
        } else {
            params.SPACEHARDQUOTA = invaliNum;
        }
        if (vmValue.spaceQuota.soft.value) {
            params.SPACESOFTQUOTA = String(this.utilService.adaptUnit(
                vmValue.spaceQuota.soft.value,
                vmValue.spaceQuota.soft.unit,
                CAPACITY_UNIT.KB,
            ).value);
        } else {
            params.SPACESOFTQUOTA = invaliNum;
        }
        if (vmValue.fileQuota.hard || vmValue.fileQuota.hardUnit) {
            params.FILEHARDQUOTA = String(vmValue.fileQuota.hard * vmValue.fileQuota.hardUnit);
        } else {
            params.FILEHARDQUOTA = invaliNum;
        }
        if (vmValue.fileQuota.soft) {
            params.FILESOFTQUOTA = String(vmValue.fileQuota.soft * vmValue.fileQuota.softUnit);
        } else {
            params.FILESOFTQUOTA = invaliNum;
        }

        if (this.data.vstoreId) {
            params.vstoreId = this.data.vstoreId;
        }
        // 前台表格不下发接口，直接return
        if (this.isFrontTable) {
            this.closeArgs.data = { ...params, ID: this.data.ID };
            this.closeArgs.refresh = false;
            return [];
        }
        cmds.push({
            name: this.str.modiyResult(this.data),
            action: this.service.modify(this.data.ID, params),
        });

        return cmds;
    }
}
